import { useEffect, useRef, useState } from "react";
import { createFileRoute, useLocation } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { Check, CircleCheck } from "lucide-react";
import { useTranslation } from "react-i18next";

import { api } from "../../convex/_generated/api";
import { EmailConfirmation } from "@/components/account/email-confirmation";
import { PaneHeader, RowAction, SettingRow } from "@/components/settings/frame";
import { authClient } from "@/lib/auth-client";

// Your profile: the name (saved as you type), the address, and this browser's
// session with signing out. Deleting the account has its own page.
export const Route = createFileRoute("/settings/profile")({
  // The email confirmation lands here (?email=confirmed, ?error=… when expired).
  validateSearch: (search: Record<string, unknown>): { email?: string; error?: string } => ({
    email: typeof search.email === "string" ? search.email : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
  }),
  component: ProfilePage,
});

type SaveState = "idle" | "saving" | "saved" | "failed";

function ProfilePage() {
  const { t, i18n } = useTranslation("settings");
  const me = useQuery(api.auth.getCurrentUser);
  const session = authClient.useSession().data?.session;
  const savedName = (me as { name?: string } | null | undefined)?.name ?? "";
  const [name, setName] = useState<string | null>(null);
  const [state, setState] = useState<SaveState>("idle");
  const input = useRef<HTMLInputElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  // From the card's menu, "Edit name" arrives here with #name.
  const hash = useLocation({ select: (l) => l.hash });
  useEffect(() => {
    if (me && hash === "name") input.current?.focus();
  }, [me, hash]);

  // A name typed but not yet sent is sent at once on leaving the field or the page.
  const pending = useRef<(() => void) | null>(null);
  function flush() {
    clearTimeout(timer.current);
    const save = pending.current;
    pending.current = null;
    save?.();
  }
  useEffect(() => flush, []);

  function onChange(value: string) {
    setName(value);
    clearTimeout(timer.current);
    pending.current = null;
    const next = value.trim();
    if (!next || next === savedName) return;
    pending.current = async () => {
      setState("saving");
      const res = await authClient.updateUser({ name: next }).catch(() => null);
      setState(res && !res.error ? "saved" : "failed");
    };
    timer.current = setTimeout(flush, 800);
  }

  if (me === undefined) return <div className="h-64 animate-pulse rounded-md bg-paper-2" />;
  if (!me) return null;

  const since = session?.createdAt
    ? new Intl.DateTimeFormat(i18n.language, { month: "long", day: "numeric" }).format(new Date(session.createdAt))
    : null;
  const device = describeBrowser(session?.userAgent ?? navigator.userAgent);

  return (
    <div>
      <PaneHeader
        title={t("nav.profile")}
        description={t("profile.description")}
        aside={
          state === "idle" ? null : (
            <span role="status" className="flex items-center gap-1.5 pt-2 text-xs text-ink-3">
              {state === "saved" ? <Check className="size-3.25" /> : null}
              {state === "saving"
                ? t("profile.saving")
                : state === "saved"
                  ? t("profile.saved")
                  : t("profile.saveFailed")}
            </span>
          )
        }
      />
      <div className="border-t border-line-strong">
        <SettingRow label={<label htmlFor="name">{t("account.name")}</label>}>
          <input
            id="name"
            ref={input}
            value={name ?? savedName}
            onChange={(e) => onChange(e.target.value)}
            onBlur={flush}
            placeholder={t("profile.namePlaceholder")}
            maxLength={80}
            autoComplete="name"
            className="h-9 w-full border-b border-line bg-transparent font-display text-xl text-ink outline-none transition-colors placeholder:text-ink-4 focus:border-ink sm:w-72"
          />
        </SettingRow>
        <SettingRow label={t("account.email")} hint={t("profile.emailHint")}>
          <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-sm text-ink [overflow-wrap:anywhere]">
            {me.email}
            {me.emailVerified ? (
              <span className="inline-flex items-center gap-1 text-xs text-[oklch(0.52_0.08_150)]">
                <CircleCheck className="size-3.25" />
                {t("profile.confirmed")}
              </span>
            ) : null}
          </span>
        </SettingRow>
        <EmailConfirmation email={me.email} verified={me.emailVerified} />
        <SettingRow
          label={t("profile.thisBrowser")}
          hint={
            since
              ? device
                ? t("profile.browserOn", { browser: device.browser, os: device.os, date: since })
                : t("profile.signedInSince", { date: since })
              : null
          }
          action={
            <RowAction
              onClick={async () => {
                await authClient.signOut();
                window.location.assign("/login");
              }}
            >
              {t("account.signOut")}
            </RowAction>
          }
        />
      </div>
    </div>
  );
}

/** "Firefox", "macOS" from a user agent; null when it can't tell. */
function describeBrowser(ua: string): { browser: string; os: string } | null {
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /Firefox\//.test(ua)
      ? "Firefox"
      : /Chrome\//.test(ua)
        ? "Chrome"
        : /Safari\//.test(ua)
          ? "Safari"
          : null;
  const os = /iPhone|iPad/.test(ua)
    ? "iOS"
    : /Android/.test(ua)
      ? "Android"
      : /Mac OS X/.test(ua)
        ? "macOS"
        : /Windows/.test(ua)
          ? "Windows"
          : /Linux/.test(ua)
            ? "Linux"
            : null;
  return browser && os ? { browser, os } : null;
}
